import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../../services/supabase';
import { ANALYTICS_EVENTS, trackEvent } from '../../utils/analytics';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userId?: string;
}

const ContactSupportModal: React.FC<ContactSupportModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  userId,
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    trackEvent(ANALYTICS_EVENTS.CONTACT_SUPPORT_OPENED, {
      page_url: pageUrl,
      has_user_email: Boolean(userEmail),
      has_user_id: Boolean(userId),
    });

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose, pageUrl, userEmail, userId]);

  const resetForm = () => {
    setSubject('');
    setMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      toast.error('Please add a subject.');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-support-request', {
        body: {
          type: 'support',
          subject: subject.trim(),
          message: message.trim(),
          userEmail,
          userId,
          pageUrl,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit support request.');
      }

      trackEvent(ANALYTICS_EVENTS.CONTACT_SUPPORT_SUBMITTED, {
        page_url: pageUrl,
        has_user_email: Boolean(userEmail),
        has_user_id: Boolean(userId),
        subject_length: subject.trim().length,
        message_length: message.trim().length,
        partial_submission: Boolean(data?.partial),
      });

      if (data?.partial) {
        toast.success('Your message was submitted!');
      } else {
        toast.success('Thanks! Your message has been sent to our team.');
      }

      handleClose();
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.CONTACT_SUPPORT_FAILED, {
        page_url: pageUrl,
        has_user_email: Boolean(userEmail),
        has_user_id: Boolean(userId),
        error_message:
          error instanceof Error ? error.message : 'Failed to submit support request.',
      });

      toast.error(error instanceof Error ? error.message : 'Failed to submit support request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100]"
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="contact-support-title"
    >
      <div className="flex min-h-full items-center justify-center overflow-y-auto p-4 sm:p-6">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2
                  id="contact-support-title"
                  className="text-xl font-semibold text-gray-900"
                >
                  Contact Support
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Send us a message and our team will review it.
                </p>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="rounded-md p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close contact support modal"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="How can we help?"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                maxLength={120}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question or issue."
                className="min-h-[140px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              onClick={handleClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
            >
              {isSubmitting ? 'Submitting...' : 'Send Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupportModal;